import { useTrackCallEvent } from './useTrackCallEvent';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { TrackedEvents, getTrackingState } from '../../features/tracking';
import { callDurationTimer } from './callDurationTimer';
import type { SessionStartedProps } from './eventProps.types';
import {
  setTrackedCallStart,
  type TrackingUserLocation,
} from '../../features/tracking/tracking.slice';
import { useVirtualBackgroundName } from '../useVirtualBackground';
import { otExceptionsStats } from './otExceptionsStats';
import { pubSubStatusStats } from './pubSubStatusStats';

type TrackCallStartEvent = (props: {
  sessionId?: string;
  dbSessionId?: string;
  identityId?: string;
  isAudioCall?: boolean;
  initialParticipantCount?: number;
}) => void;

const startCallLocationProps = (
  userLocation: TrackingUserLocation | null,
): Pick<
  SessionStartedProps,
  'country' | 'region' | 'city' | 'latitude' | 'longitude'
> => {
  if (!userLocation) return {};

  const { location } = userLocation;
  return {
    country: location.country_name,
    region: location.region_name,
    city: location.city ?? null,
    latitude: location.latitude ?? null,
    longitude: location.longitude ?? null,
  };
};

export const useTrackCallStartEvent = (): TrackCallStartEvent => {
  const dispatch = useAppDispatch();
  const trackCallEvent = useTrackCallEvent();

  const { connectionTestResult, userLocation } =
    useAppSelector(getTrackingState);

  const backgroundEffect = useVirtualBackgroundName();

  return ({
    sessionId,
    dbSessionId,
    identityId,
    isAudioCall,
    initialParticipantCount,
  }): void => {
    dispatch(setTrackedCallStart());

    callDurationTimer.start();
    otExceptionsStats.collect();
    pubSubStatusStats.collect();

    // Only providers pass initialParticipantCount (from useStartCall); patient join omits it.
    const optionalParticipantCount =
      initialParticipantCount !== undefined ? { initialParticipantCount } : {};

    const data: SessionStartedProps = {
      sessionId,
      personUuid: identityId,
      preCallConnection: connectionTestResult,
      audioOnly: isAudioCall,
      ...optionalParticipantCount,
    };

    const segmentData: SessionStartedProps = {
      sessionId: dbSessionId,
      channel: sessionId,
      personUuid: identityId,
      preCallConnection: connectionTestResult,
      audioOnly: isAudioCall,
      backgroundEffect,
      ...optionalParticipantCount,
      ...startCallLocationProps(userLocation),
    };
    trackCallEvent(TrackedEvents.StartCall, data, segmentData);
  };
};
